import React from "react";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";

import {
  AccordionSummary,
  AccordionDetails,
  AccordionProps as MuiAccordionProps,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import {
  BabyPeekAccordionAnswer,
  BabyPeekAccordionQuestion,
  BabyPeekGameAccordionTitle,
  BabyPeekGameAccordionTitleGrid,
  BabyPeekGamesAccordionText,
  BabyPeekStyledAccordion,
} from "./BabyPeekAccordion.styles";

import { BabyPeekDescriptionSectionText } from "./BabyPeekTrait.styles";
import { BabyPeekGameszList } from "./BabyPeekIcons/BabyPeekGamesList";
import { BabyPeekGamesGuessTheLittleOne } from "./BabyPeekIcons/BabyPeekGamesGuessLittleOne";
import { BabyPeekButton } from "./BabyPeekButton";
import { BabyPeekGamesWriting } from "./BabyPeekIcons/BabyPeekGamesWriting";
import { BabyPeekGamesGifts } from "./BabyPeekIcons/BabyPeekGamesGIft";
import { BabyPeekGamesPeekABoo } from "./BabyPeekIcons/BabyPeekGamesPeekABook";
import { BabyPeekGamesTraitReveal } from "./BabyPeekIcons/BabyPeekGamesTraitReveal";
import { BabyPeekDownloadGames } from "./Services/BabyPeekService";

export const getBabyPeekFAQAccordion = (): BabyPeekAccordionProps[] => [
  {
    title: <Trans i18nKey="babyPeek.faq.available.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans i18nKey="babyPeek.faq.available.contentIntro" />
          <ul>
            <li>
              <Trans i18nKey="babyPeek.faq.available.twinPregnancy" />
            </li>
            <li>
              <Trans i18nKey="babyPeek.faq.available.eggDonorSurrogate" />
            </li>
            <li>
              <Trans i18nKey="babyPeek.faq.available.noCallOrRedraw" />
            </li>
            <li>
              <Trans i18nKey="babyPeek.faq.available.rareCircumstances" />
            </li>
          </ul>
        </BabyPeekDescriptionSectionText>
        <BabyPeekDescriptionSectionText style={{ paddingTop: "24px" }}>
          <Trans
            i18nKey="babyPeek.faq.available.contact"
            components={{ link: <Link to="/client/contactUs" /> }}
          />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "available",
  },
  {
    title: <Trans i18nKey="babyPeek.faq.health.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans
            i18nKey="babyPeek.faq.health.content"
            components={{ sup: <sup /> }}
          />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "health",
  },
  {
    title: <Trans i18nKey="babyPeek.faq.predict.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans
            i18nKey="babyPeek.faq.predict.content"
            components={{ bold: <span style={{ fontWeight: 700 }} /> }}
          />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "predict",
  },
  {
    title: <Trans i18nKey="babyPeek.faq.paternity.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans i18nKey="babyPeek.faq.paternity.content" />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "paternity",
  },
  {
    title: <Trans i18nKey="babyPeek.faq.contact.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans
            i18nKey="babyPeek.faq.contact.content"
            components={{ link: <Link to="/client/contactUs" /> }}
          />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "contact",
  },
  {
    title: <Trans i18nKey="babyPeek.faq.provider.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans
            i18nKey="babyPeek.faq.provider.content"
            components={{ sup: <sup /> }}
          />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "provider",
  },
  {
    title: <Trans i18nKey="babyPeek.faq.results.title" />,
    content: (
      <>
        <BabyPeekDescriptionSectionText>
          <Trans i18nKey="babyPeek.faq.results.content" />
        </BabyPeekDescriptionSectionText>
      </>
    ),
    key: "results",
  },
];

export const babyPeekGameAccordion = (
  babypeekPK: string,
  auth?: string,
): BabyPeekAccordionProps[] => [
  {
    title: (
      <BabyPeekGameAccordionTitleGrid>
        <BabyPeekGameszList />{" "}
        <BabyPeekGameAccordionTitle>
          <Trans i18nKey="babyPeek.games.guessTheLittleOne.title" />
        </BabyPeekGameAccordionTitle>
      </BabyPeekGameAccordionTitleGrid>
    ),
    content: (
      <>
        <BabyPeekGamesAccordionText>
          <div>
            <Trans
              i18nKey="babyPeek.games.guessTheLittleOne.description"
              components={{ span: <span /> }}
            />
          </div>
          <br />
          <BabyPeekGamesGuessTheLittleOne />
          <BabyPeekButton
            variant="filled"
            content={
              <>
                <Trans i18nKey="babyPeek.buttons.downloadPreview" />
              </>
            }
            onClick={async () => {
              const downloadLinkResponse = await BabyPeekDownloadGames(
                babypeekPK,
                "GuessTheLittleOne",
                "Preview",
                auth,
              );
              if (downloadLinkResponse) {
                const link = await downloadLinkResponse.json();
                const tempAnchor = document.createElement("a");
                tempAnchor.download = "TraitsRevealAnswer.pdf";
                const downloadUrl = link.s3_url;
                if (downloadUrl) {
                  tempAnchor.href = downloadUrl;
                  tempAnchor.click();
                }
                tempAnchor.remove();
              }
            }}
            style={{
              justifySelf: "center",
              marginTop: "-90px",
            }}
          />
          <br />
        </BabyPeekGamesAccordionText>
      </>
    ),
    key: "guessLittleOne",
  },
  {
    title: (
      <BabyPeekGameAccordionTitleGrid>
        <BabyPeekGamesWriting />{" "}
        <BabyPeekGameAccordionTitle>
          <Trans i18nKey="babyPeek.games.peekABoo.title" />
        </BabyPeekGameAccordionTitle>
      </BabyPeekGameAccordionTitleGrid>
    ),
    content: (
      <>
        <BabyPeekGamesAccordionText>
          <div>
            <Trans i18nKey="babyPeek.games.peekABoo.description" />
          </div>
          <br />
          <BabyPeekGamesPeekABoo />
          <BabyPeekButton
            variant="filled"
            content={
              <>
                <Trans i18nKey="babyPeek.buttons.downloadPreview" />
              </>
            }
            onClick={async () => {
              const downloadLinkResponse = await BabyPeekDownloadGames(
                babypeekPK,
                "PeekABoo",
                "Preview",
                auth,
              );
              if (downloadLinkResponse) {
                const link = await downloadLinkResponse.json();
                const tempAnchor = document.createElement("a");
                tempAnchor.download = "TraitsRevealAnswer.pdf";
                const downloadUrl = link.s3_url;
                if (downloadUrl) {
                  tempAnchor.href = downloadUrl;
                  tempAnchor.click();
                }
                tempAnchor.remove();
              }
            }}
            style={{
              justifySelf: "center",
              marginTop: "-90px",
            }}
          />
          <br />
        </BabyPeekGamesAccordionText>
      </>
    ),
    key: "peekABoo",
  },
  {
    title: (
      <BabyPeekGameAccordionTitleGrid>
        <BabyPeekGamesGifts />{" "}
        <BabyPeekGameAccordionTitle>
          <Trans i18nKey="babyPeek.games.traitsReveal.title" />
        </BabyPeekGameAccordionTitle>
      </BabyPeekGameAccordionTitleGrid>
    ),
    content: (
      <>
        <BabyPeekGamesAccordionText>
          <div>
            <Trans i18nKey="babyPeek.games.traitsReveal.description" />
          </div>
          <br />
          <BabyPeekGamesTraitReveal />
          <BabyPeekButton
            variant="filled"
            content={
              <>
                <Trans i18nKey="babyPeek.buttons.downloadPreview" />
              </>
            }
            onClick={async () => {
              const downloadLinkResponse = await BabyPeekDownloadGames(
                babypeekPK,
                "TraitsReveal",
                "Preview",
                auth,
              );
              if (downloadLinkResponse) {
                const link = await downloadLinkResponse.json();
                const tempAnchor = document.createElement("a");
                tempAnchor.download = "TraitsRevealAnswer.pdf";
                const downloadUrl = link.s3_url;
                if (downloadUrl) {
                  tempAnchor.href = downloadUrl;
                  tempAnchor.click();
                }
                tempAnchor.remove();
              }
            }}
            style={{
              justifySelf: "center",
              marginTop: "-90px",
            }}
          />
          <br />
        </BabyPeekGamesAccordionText>
      </>
    ),
    key: "babypeekGamesTraitReveal",
  },
];

export interface BabyPeekAccordionProps extends Omit<
  MuiAccordionProps,
  "children" | "content" | "title"
> {
  title: React.ReactNode;
  content: React.ReactNode;
  key: string;
}

export const BabyPeekAccordions: React.FC<{
  data: BabyPeekAccordionProps[];
}> = ({ data }) => {
  const [expanded, setExpanded] = React.useState<string | false>("");

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const defaultAccordions = data.map((accordionSections) => (
    <BabyPeekStyledAccordion
      expanded={expanded === accordionSections.key}
      onChange={handleChange(accordionSections.key)}
      key={accordionSections.key}
    >
      <AccordionSummary
        expandIcon={
          expanded === accordionSections.key ? (
            <FontAwesomeIcon
              icon={faChevronUp}
              style={{ color: "#2D3748", height: "20px" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={faChevronRight}
              style={{ color: "#2D3748", height: "20px" }}
            />
          )
        }
        aria-controls={`${accordionSections.key}-panel-content`}
        id={`${accordionSections.key}-help`}
      >
        <BabyPeekAccordionQuestion>
          {accordionSections.title}
        </BabyPeekAccordionQuestion>
      </AccordionSummary>
      <AccordionDetails>
        <BabyPeekAccordionAnswer>
          {accordionSections.content}
        </BabyPeekAccordionAnswer>
      </AccordionDetails>
    </BabyPeekStyledAccordion>
  ));

  return <>{defaultAccordions}</>;
};
